import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion';
import { LINKS, TAGLINE_PRIMARY } from '@/data/content';
import { Link } from 'react-router-dom';

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.2 } }
};
const line = {
  hidden: { y: '115%' },
  show: { y: '0%', transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};
const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

export default function HeroSection({ started }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [coarse, setCoarse] = useState(true);

  useEffect(() => {
    setCoarse(window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 768);
  }, []);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });

  const bgX = useTransform(sx, (v) => v * -8);
  const bgY = useTransform(sy, (v) => v * -6);
  const fgX = useTransform(sx, (v) => v * 34);
  const fgY = useTransform(sy, (v) => v * 26);

  const onMouseMove = (e) => {
    if (reduce || coarse) return;
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="scanlines relative flex min-h-screen flex-col overflow-hidden"
      aria-label="Welcome to ARC TOONS"
    >
      <motion.div className="absolute inset-[-3%]" style={coarse || reduce ? {} : { x: bgX, y: bgY }}>
        <motion.img
          src="/assets/arctoons-web-background-opt.webp"
          alt="Toon City — the ARC TOONS universe skyline"
          fetchpriority="high"
          className="h-full w-full object-cover"
          initial={{ scale: 1.14, opacity: 0 }}
          animate={started ? { scale: 1.05, opacity: 1 } : {}}
          transition={{ duration: 1.6, ease: 'easeOut' }}
        />
      </motion.div>
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to bottom, rgba(5,11,24,0.42) 0%, rgba(5,11,24,0.1) 35%, rgba(5,11,24,0.55) 72%, #050B18 100%)'
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 hidden md:block"
        style={{
          background:
            'linear-gradient(90deg, rgba(5,11,24,0.78) 0%, rgba(5,11,24,0.42) 42%, rgba(5,11,24,0.08) 68%, transparent 85%)'
        }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute left-[12%] top-[-20%] h-[120%] w-[30%] rotate-12"
        style={{
          background: 'linear-gradient(to bottom, transparent, rgba(0,200,255,0.16), transparent)',
          filter: 'blur(6px)'
        }}
        animate={started && !reduce ? { opacity: [0.15, 0.6, 0.15] } : {}}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute right-[28%] top-[-20%] h-[120%] w-[16%] -rotate-6"
        style={{
          background: 'linear-gradient(to bottom, transparent, rgba(255,181,46,0.12), transparent)',
          filter: 'blur(8px)'
        }}
        animate={started && !reduce ? { opacity: [0.5, 0.15, 0.5] } : {}}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      />

      <motion.div
        className="relative z-[3] mx-auto flex w-full max-w-[1400px] flex-1 flex-col justify-center px-6 pb-24 pt-36 sm:px-10 lg:pt-28"
        variants={container}
        initial="hidden"
        animate={started ? 'show' : 'hidden'}
        style={coarse || reduce ? {} : { x: fgX, y: fgY }}
      >
        <motion.div variants={fade} className="mb-5 flex items-center gap-3">
          <img src="/assets/arctoons-logo-opt.webp" alt="" aria-hidden="true" className="h-9 w-9 object-contain" />
          <span
            className="rounded-full border border-electric/25 bg-navy/60 px-4 py-2 text-center font-mono text-[9px] uppercase tracking-[0.28em] text-electric sm:text-[11px] sm:tracking-[0.4em]"
            style={{ backdropFilter: 'blur(8px)' }}
          >
            {TAGLINE_PRIMARY}
          </span>
        </motion.div>

        <h1 className="font-display text-[9.5vw] font-extrabold uppercase leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5.6rem]">
          <span className="block overflow-hidden pb-1">
            <motion.span variants={line} className="block" data-testid="hero-headline-line1">
              WELCOME TO
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              variants={line}
              data-testid="hero-headline-line2"
              className="text-glow-blue block bg-gradient-to-r from-arc via-electric to-gold bg-clip-text text-transparent"
            >
              ARCTOONS
            </motion.span>
          </span>
        </h1>

        <motion.p
          variants={fade}
          data-testid="hero-tagline"
          className="mt-5 inline-block max-w-md rounded-2xl bg-navy/55 px-5 py-4 font-mono text-[11px] uppercase leading-loose tracking-[0.3em] text-gold sm:text-xs"
          style={{ backdropFilter: 'blur(8px)' }}
        >
          ONE CHAIN.
          <br />
          MANY TOONS.
          <br />
          INFINITE STORIES.
        </motion.p>

        <motion.p
          variants={fade}
          className="mt-5 inline-block max-w-lg rounded-2xl bg-navy/55 px-5 py-4 text-left text-sm leading-relaxed text-snow/75 sm:text-base"
          style={{ backdropFilter: 'blur(8px)' }}
          data-testid="hero-description"
        >
          ARC TOONS is a colorful universe of characters, stories and digital culture born on Arc.
        </motion.p>

        <motion.div variants={fade} className="mt-9 flex flex-wrap items-center gap-4" data-testid="hero-cta-row">
          <Link
            to="/universe"
            data-testid="hero-enter-toonverse-button"
            className="light-sweep rounded-full bg-gradient-to-r from-arc via-electric to-arc px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_32px_rgba(0,200,255,0.45)] transition-all duration-200 hover:scale-[1.04] hover:shadow-[0_0_50px_rgba(0,200,255,0.7)] sm:text-sm"
          >
            ENTER THE TOONVERSE
          </Link>
          <Link
            to="/toons"
            data-testid="hero-explore-toons-button"
            className="rounded-full border border-electric/40 bg-navy/40 px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.2em] text-electric transition-all duration-200 hover:scale-[1.04] hover:border-electric hover:bg-electric/10 sm:text-sm"
          >
            EXPLORE TOONS
          </Link>
          <span className="flex items-center gap-3 rounded-full bg-navy/85 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-snow/70" style={{ backdropFilter: 'blur(8px)' }}>
            <a
              href={LINKS.buy}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-buy-link"
              className="border-b border-gold/50 pb-0.5 text-gold transition-colors hover:text-gold/80"
            >
              BUY
            </a>
            <a
              href={LINKS.chart}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-chart-link"
              className="border-b border-electric/50 pb-0.5 text-electric transition-colors hover:text-electric/80"
            >
              CHART
            </a>
          </span>
        </motion.div>
      </motion.div>

      <motion.div
        className="relative z-[3] mx-auto mb-8 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={started ? { opacity: 1 } : {}}
        transition={{ delay: 1.6, duration: 0.8 }}
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-snow/50">SCROLL TO ENTER</span>
        <motion.span
          aria-hidden="true"
          className="h-8 w-px bg-gradient-to-b from-electric to-transparent"
          animate={{ scaleY: [1, 0.4, 1], opacity: [1, 0.4, 1] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>
    </section>
  );
}
